import { Database } from 'lucide-react'
import { ShowUsecaseTablesResult, UsecaseTablesMap } from 'poc-ai-biz-irdb_domain-model'
import {
  UsecaseTableCompanyOverviewOrganism,
  UsecaseTableBsHighlightOrganism,
  UsecaseTablePlHighlightOrganism,
  UsecaseTableCfHighlightOrganism,
  UsecaseTableExecutiveOverviewOrganism,
  UsecaseTableExecutivesOrganism,
  UsecaseTableExecutiveCompensationOrganism,
  UsecaseTableBusinessDescriptionOrganism,
  UsecaseTableBusinessRisksOrganism,
  UsecaseTableHistoryOrganism,
  UsecaseTableMajorSubsidiariesOrganism,
  UsecaseTableOfficesOrganism,
  UsecaseTableMajorShareholdersOrganism,
} from '../../../../organisms/UsecaseTableOrganism/_gen'
import { getUsecaseTableAppearanceConfiguration, UsecaseTableMapKeys } from '../../model/usecaseTableAppearanceConfiguration'

export type SourceDocumentShowUsecaseTableCardPropsContent = {
  result: ShowUsecaseTablesResult
  filerName: string
  tables: {
    tableName: keyof UsecaseTablesMap
    displayName: string
    icon: React.ReactNode
    fiscalYears: number[]
  }[]
  usecaseTablesMap: UsecaseTablesMap
  totalDataCount: number
}

export type SourceDocumentShowUsecaseTableCardProps = SourceDocumentShowUsecaseTableCardPropsContent & {
  isExpanded: boolean
  onClick: () => void
}

/**
 * UsecaseTablesMapを企業ごとにグループ化し、UIカード表示用のプロパティに変換する
 *
 * @param usecaseTablesMap - 複数企業・複数テーブルのデータが混在したマップ
 * @param result - 元のShowUsecaseTablesResultオブジェクト
 * @returns 企業ごとにグループ化されたカードプロパティの配列（isExpanded, onClickは含まない）
 */
export const mapToSourceDocumentShowUsecaseTableCardContents = (
  usecaseTablesMap: UsecaseTablesMap,
  result: ShowUsecaseTablesResult,
): SourceDocumentShowUsecaseTableCardPropsContent[] => {
  // UsecaseTablesMapの全エントリから、企業名でグループ化するための中間データを収集
  const allTableRowsWithMetadata = Object.entries(usecaseTablesMap)
    .filter((entry): entry is [keyof UsecaseTablesMap, NonNullable<(typeof entry)[1]>] => entry[1] !== undefined)
    .flatMap(([tableNameKey, tableRows]) =>
      tableRows.map((row) => ({
        tableNameKey: tableNameKey as keyof UsecaseTablesMap,
        filerName: row.filerName ?? 'Unknown Company',
        fiscalYearEndYear: row.fiscalYearEndYear,
        row,
      })),
    )

  // 企業名でグループ化
  const rowsGroupedByFilerName = allTableRowsWithMetadata.reduce((groupedMap, rowMetadata) => {
    const existingRows = groupedMap.get(rowMetadata.filerName) ?? []
    return groupedMap.set(rowMetadata.filerName, [...existingRows, rowMetadata])
  }, new Map<string, typeof allTableRowsWithMetadata>())

  // 各企業について、カードプロパティを生成
  return Array.from(rowsGroupedByFilerName.entries()).map(([filerName, rowsForThisFiler]) => {
    // この企業が持つテーブル情報を構築
    const tablesForThisFiler = Array.from(
      rowsForThisFiler
        .reduce((tableMap, rowMetadata) => {
          const existingTableInfo = tableMap.get(rowMetadata.tableNameKey)
          const fiscalYears = existingTableInfo?.fiscalYears ?? []

          return tableMap.set(rowMetadata.tableNameKey, {
            tableNameKey: rowMetadata.tableNameKey,
            fiscalYears: [...fiscalYears, rowMetadata.fiscalYearEndYear],
          })
        }, new Map<keyof UsecaseTablesMap, { tableNameKey: keyof UsecaseTablesMap; fiscalYears: number[] }>())
        .values(),
    ).map((tableInfo) => {
      const appearanceConfiguration = getUsecaseTableAppearanceConfiguration(tableInfo.tableNameKey)
      return {
        tableName: tableInfo.tableNameKey,
        displayName: appearanceConfiguration.displayName,
        icon: appearanceConfiguration.icon,
        fiscalYears: Array.from(new Set(tableInfo.fiscalYears)).sort((a, b) => a - b),
      }
    })

    // この企業のデータだけをフィルタした UsecaseTablesMap を作成
    const usecaseTablesMapFilteredByFilerName = Object.entries(usecaseTablesMap)
      .filter((entry): entry is [keyof UsecaseTablesMap, NonNullable<(typeof entry)[1]>] => entry[1] !== undefined)
      .reduce((filteredMap, [tableNameKey, tableRows]) => {
        const rowsForThisFilerInThisTable = tableRows.filter((row) => row.filerName === filerName)
        if (rowsForThisFilerInThisTable.length > 0) {
          filteredMap[tableNameKey] = rowsForThisFilerInThisTable as any
        }
        return filteredMap
      }, {} as UsecaseTablesMap)

    // 全テーブルのデータ件数を合計
    const totalDataCountForThisFiler = Object.values(usecaseTablesMapFilteredByFilerName).reduce((sum, tableRows) => sum + (tableRows?.length ?? 0), 0)

    return {
      result,
      filerName,
      tables: tablesForThisFiler,
      usecaseTablesMap: usecaseTablesMapFilteredByFilerName,
      totalDataCount: totalDataCountForThisFiler,
    }
  })
}

export const SourceDocumentShowUsecaseTableCard: React.FC<SourceDocumentShowUsecaseTableCardProps> = ({
  filerName,
  tables,
  usecaseTablesMap,
  totalDataCount,
  isExpanded,
  onClick,
}) => {
  return (
    <button
      onClick={onClick}
      className="block p-3 border border-gray-200 rounded-md bg-white hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
    >
      <div className="flex items-start gap-2">
        <Database className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-800">{filerName}</div>
          <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-1">
            {tables.map((table, tableIndex) =>
              !isExpanded ? (
                <span key={tableIndex} className="inline-flex items-center gap-1">
                  {table.icon}
                  {table.displayName}
                  {tableIndex < tables.length - 1 && ','}
                </span>
              ) : (
                (() => {
                  console.log('Rendering expanded table:', { table, filerName, usecaseTablesMap })
                  switch (table.tableName) {
                    case 'usecaseTableCompanyOverview': {
                      const contents = usecaseTablesMap[table.tableName]?.filter((item) => item.filerName === filerName)
                      return contents && <UsecaseTableCompanyOverviewOrganism defaultExpanded={true} status="completed" contents={contents} />
                    }
                    case 'usecaseTableBsHighlight': {
                      const contents = usecaseTablesMap[table.tableName]?.filter((item) => item.filerName === filerName)
                      return contents && <UsecaseTableBsHighlightOrganism defaultExpanded={true} status="completed" contents={contents} />
                    }
                    case 'usecaseTablePlHighlight': {
                      const contents = usecaseTablesMap[table.tableName]?.filter((item) => item.filerName === filerName)
                      return contents && <UsecaseTablePlHighlightOrganism defaultExpanded={true} status="completed" contents={contents} />
                    }
                    case 'usecaseTableCfHighlight': {
                      const contents = usecaseTablesMap[table.tableName]?.filter((item) => item.filerName === filerName)
                      return contents && <UsecaseTableCfHighlightOrganism defaultExpanded={true} status="completed" contents={contents} />
                    }
                    case 'usecaseTableExecutiveOverview': {
                      const contents = usecaseTablesMap[table.tableName]?.filter((item) => item.filerName === filerName)
                      return contents && <UsecaseTableExecutiveOverviewOrganism defaultExpanded={true} status="completed" contents={contents} />
                    }
                    case 'usecaseTableExecutives': {
                      const contents = usecaseTablesMap[table.tableName]?.filter((item) => item.filerName === filerName)
                      return contents && <UsecaseTableExecutivesOrganism defaultExpanded={true} status="completed" contents={contents} />
                    }
                    case 'usecaseTableExecutiveCompensation': {
                      const contents = usecaseTablesMap[table.tableName]?.filter((item) => item.filerName === filerName)
                      return contents && <UsecaseTableExecutiveCompensationOrganism defaultExpanded={true} status="completed" contents={contents} />
                    }
                    case 'usecaseTableBusinessDescription': {
                      const contents = usecaseTablesMap[table.tableName]?.filter((item) => item.filerName === filerName)
                      return contents && <UsecaseTableBusinessDescriptionOrganism defaultExpanded={true} status="completed" contents={contents} />
                    }
                    case 'usecaseTableBusinessRisks': {
                      const contents = usecaseTablesMap[table.tableName]?.filter((item) => item.filerName === filerName)
                      return contents && <UsecaseTableBusinessRisksOrganism defaultExpanded={true} status="completed" contents={contents} />
                    }
                    case 'usecaseTableHistory': {
                      const contents = usecaseTablesMap[table.tableName]?.filter((item) => item.filerName === filerName)
                      return contents && <UsecaseTableHistoryOrganism defaultExpanded={true} status="completed" contents={contents} />
                    }
                    case 'usecaseTableMajorSubsidiaries': {
                      const contents = usecaseTablesMap[table.tableName]?.filter((item) => item.filerName === filerName)
                      return contents && <UsecaseTableMajorSubsidiariesOrganism defaultExpanded={true} status="completed" contents={contents} />
                    }
                    case 'usecaseTableOffices': {
                      const contents = usecaseTablesMap[table.tableName]?.filter((item) => item.filerName === filerName)
                      return contents && <UsecaseTableOfficesOrganism defaultExpanded={true} status="completed" contents={contents} />
                    }
                    case 'usecaseTableMajorShareholders': {
                      const contents = usecaseTablesMap[table.tableName]?.filter((item) => item.filerName === filerName)
                      return contents && <UsecaseTableMajorShareholdersOrganism defaultExpanded={true} status="completed" contents={contents} />
                    }
                    default:
                      console.error(`Unknown table name: ${table.tableName}`)
                      return null
                  }
                })()
              ),
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1">{totalDataCount}件</div>
        </div>
      </div>
    </button>
  )
}
